// Marketplace item detail: cover, description, creator, what you get, Buy and
// (for worlds) "Try it in 3D". Opened from a storefront card, or directly via
// /marketplace?item=<id> so a listing can be shared.
//
// No 3D turntable for models/avatars on purpose: it would need the model file
// URL in the browser, and the storefront keeps asset_url private so a paid
// asset cannot be downloaded for free. Worlds get the preview route instead,
// which renders the scene without handing over a copy.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Box, Clock, Crown, Eye, Package, ShoppingBag, X } from 'lucide-react';
import { formatMoney } from './buyListing';

export interface SheetListing {
  id: string;
  creator_id: string;
  title: string;
  description: string | null;
  kind: string;
  price_cents: number;
  currency: string;
  thumbnail_url: string | null;
  sales_count: number;
  sale_type?: 'copy' | 'original';
  purchasable?: boolean;
}

const WHERE: Record<string, string> = {
  world: 'your worlds, as a world you can open and edit',
  avatar: 'your avatars, ready to wear',
};

const ListingSheet: React.FC<{
  listing: SheetListing;
  creatorName?: string | null;
  owned: boolean;
  mine: boolean;
  purchasesOpen: boolean;
  buying: boolean;
  onBuy: () => void;
  onTry: () => void;
  onClose: () => void;
}> = ({ listing: l, creatorName, owned, mine, purchasesOpen, buying, onBuy, onTry, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const isWorld = l.kind === 'world';
  const original = l.sale_type === 'original';
  const price = formatMoney(l.price_cents, l.currency);

  let action: React.ReactNode;
  if (owned) action = <span className="mk-sheet-state">You own this</span>;
  else if (mine) action = <span className="mk-sheet-state">Your listing</span>;
  else if (!purchasesOpen || l.purchasable === false) {
    action = <span className="mk-sheet-state" title="The creator hasn't finished payout setup yet."><Clock size={14} /> Coming soon</span>;
  } else {
    action = (
      <button className="mk-primary mk-sheet-buy" onClick={onBuy} disabled={buying}>
        <ShoppingBag size={15} /> Buy for {price}
      </button>
    );
  }

  return createPortal(
    <div className="mk-sheet-overlay" onClick={onClose}>
      <article className="mk-sheet" role="dialog" aria-modal="true" aria-label={l.title} onClick={(e) => e.stopPropagation()}>
        <button className="mk-sheet-x" onClick={onClose} aria-label="Close"><X size={18} /></button>

        <div className="mk-sheet-cover">
          {l.thumbnail_url ? <img src={l.thumbnail_url} alt="" /> : <Package size={34} />}
          <span className="mk-kind">{l.kind}</span>
          {original && <span className="mk-sheet-one"><Crown size={12} /> One of one</span>}
          {isWorld && (
            <button className="mk-sheet-try-cover" onClick={onTry}>
              <Eye size={15} /> Try it in 3D
            </button>
          )}
        </div>

        <div className="mk-sheet-body">
          <header className="mk-sheet-head">
            <div>
              <h2>{l.title}</h2>
              <p className="mk-sheet-by">
                {creatorName
                  ? <>by <Link to={`/u/${encodeURIComponent(creatorName)}`} onClick={onClose}>@{creatorName}</Link></>
                  : 'by a GathrXR builder'}
                {l.sales_count > 0 && <> · {l.sales_count} sold</>}
              </p>
            </div>
            <span className="mk-sheet-price">{price}</span>
          </header>

          <div className="mk-sheet-actions">
            {action}
            {isWorld && (
              <button className="mk-ghost" onClick={onTry}><Eye size={14} /> Walk through it</button>
            )}
          </div>

          {l.description
            ? <p className="mk-sheet-desc">{l.description}</p>
            : <p className="mk-sheet-desc mk-sheet-muted">The creator hasn't written a description yet.</p>}

          <dl className="mk-sheet-facts">
            <div>
              <dt><Box size={13} /> What you get</dt>
              <dd>
                {original
                  ? 'The original itself. It moves to your account and leaves the seller’s — there is only one.'
                  : `Your own copy, delivered to ${WHERE[l.kind] || 'My Uploads, ready to place in any world'}.`}
              </dd>
            </div>
            {isWorld && (
              <div>
                <dt><Eye size={13} /> Try before you buy</dt>
                <dd>Walk around the world first. The preview is read-only and private to you.</dd>
              </div>
            )}
          </dl>
        </div>
      </article>
    </div>,
    document.body,
  );
};

export default ListingSheet;
